import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

// Normalize a string for comparison (lowercase, trim, collapse spaces)
const normalizeForComparison = (str) => {
  if (!str) return ''
  return str.toLowerCase().trim().replace(/\s+/g, ' ')
}

export function Combobox({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  emptyText = "No results found.",
  searchPlaceholder = "Search...",
  allowCreate = false,
  onCreateNew,
  className,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const [isCreating, setIsCreating] = React.useState(false)

  // Check if a value already exists (case-insensitive, trimmed, space-normalized)
  const valueExists = React.useCallback((val) => {
    if (!val || !options.length) return false
    const normalizedVal = normalizeForComparison(val)
    return options.some(opt => {
      const normalizedValue = normalizeForComparison(opt.value)
      const normalizedLabel = normalizeForComparison(opt.label)
      const normalizedKey = normalizeForComparison(opt.key || '')
      return normalizedValue === normalizedVal || 
             normalizedLabel === normalizedVal || 
             normalizedKey === normalizedVal
    })
  }, [options])

  // Find existing option that matches the search value
  const findMatchingOption = React.useCallback((val) => {
    if (!val || !options.length) return null
    const normalizedVal = normalizeForComparison(val)
    return options.find(opt => {
      const normalizedValue = normalizeForComparison(opt.value)
      const normalizedLabel = normalizeForComparison(opt.label)
      const normalizedKey = normalizeForComparison(opt.key || '')
      return normalizedValue === normalizedVal || 
             normalizedLabel === normalizedVal || 
             normalizedKey === normalizedVal
    })
  }, [options])

  const handleSelect = (currentValue) => {
    const newValue = currentValue === value ? "" : currentValue
    onValueChange(newValue)
    setOpen(false)
    setSearchValue("")
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && allowCreate && searchValue && !isCreating) {
      e.preventDefault()
      const normalizedSearch = searchValue.trim().replace(/\s+/g, ' ') // Normalize whitespace
      
      // Check if this value already exists
      const existingOption = findMatchingOption(normalizedSearch)
      if (existingOption) {
        // Select the existing option instead of creating a new one
        onValueChange(existingOption.value)
        setSearchValue("")
        setOpen(false)
        return
      }
      
      // Create new value
      setIsCreating(true)
      onCreateNew?.(normalizedSearch)
      onValueChange(normalizedSearch)
      setSearchValue("")
      setOpen(false)
      // Reset creating state after a short delay to prevent double-fires
      setTimeout(() => setIsCreating(false), 300)
    }
  }

  const displayValue = React.useMemo(() => {
    // First check if value matches an option
    const found = options.find((opt) => opt.value === value)
    if (found) return found.label
    // If not found in options but has a value, display it (for newly created items)
    return value || ""
  }, [value, options])

  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return options
    const normalizedSearch = normalizeForComparison(searchValue)
    return options.filter((opt) =>
      normalizeForComparison(opt.label).includes(normalizedSearch)
    )
  }, [options, searchValue])

  // Show create option only if no match exists (case-insensitive)
  const showCreateOption = allowCreate && searchValue && !valueExists(searchValue.trim())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {displayValue || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
            onKeyDown={handleKeyDown}
          />
          <CommandEmpty>
            {showCreateOption ? (
              <div className="p-2 text-sm text-muted-foreground">
                Press Enter to create &ldquo;{searchValue.trim()}&rdquo;
              </div>
            ) : (
              emptyText
            )}
          </CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {filteredOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={handleSelect}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
